import {
  Alert,
  Badge,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';

import { api } from '../api';
import { apiHook } from '../treaty';

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
});

function today() {
  return dayjs().format('YYYY-MM-DD');
}

export function ExpensesPage() {
  const queryClient = useQueryClient();
  const categoriesQuery = apiHook.useQuery((client) => client['expense-categories'].get());
  const expensesQuery = apiHook.useQuery((client) =>
    client.expenses.get({ query: { limit: 100 } }),
  );
  const [expenseDate, setExpenseDate] = useState(today);
  const [amount, setAmount] = useState<string | number>('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [payee, setPayee] = useState('');
  const [notes, setNotes] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const categories = categoriesQuery.data ?? [];

  async function submitCategory() {
    const name = categoryName.trim();
    if (!name) return;

    setSavingCategory(true);
    const response = await api.api.api.v1['expense-categories'].post({ name });
    setSavingCategory(false);
    if (response.error || !response.data) {
      notifications.show({
        color: 'red',
        message: 'That category could not be added. It may already exist.',
      });
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    setCategoryId(response.data.id);
    setCategoryName('');
    setShowCategoryForm(false);
    notifications.show({ color: 'green', message: `${response.data.name} added.` });
  }

  async function submitExpense(event: React.FormEvent) {
    event.preventDefault();
    const numericAmount = typeof amount === 'number' ? amount : Number(amount);
    const amountPaise = Math.round(numericAmount * 100);
    if (!categoryId || !Number.isFinite(numericAmount) || amountPaise < 1) {
      notifications.show({ color: 'red', message: 'Enter a valid amount and category.' });
      return;
    }

    setSavingExpense(true);
    const response = await api.api.api.v1.expenses.post({
      expenseDate,
      amountPaise,
      categoryId,
      payee,
      ...(notes.trim() ? { notes } : {}),
    });
    setSavingExpense(false);
    if (response.error) {
      notifications.show({ color: 'red', message: 'The expense could not be saved.' });
      return;
    }

    setAmount('');
    setPayee('');
    setNotes('');
    await queryClient.invalidateQueries({ queryKey: ['expenses'] });
    notifications.show({ color: 'green', message: 'Expense saved.' });
  }

  return (
    <Stack gap='lg' maw={1200} mx='auto'>
      <Group justify='space-between' align='end'>
        <div>
          <Title order={1}>Expenses</Title>
          <Text c='dimmed' mt={5}>
            Record and review company spending in one shared ledger.
          </Text>
        </div>
        <Badge variant='light' size='lg'>
          {expensesQuery.data?.length ?? 0} recent expenses
        </Badge>
      </Group>

      <Paper withBorder radius='lg' p='xl'>
        <form onSubmit={submitExpense}>
          <Stack gap='md'>
            <div>
              <Title order={3}>Add expense</Title>
              <Text size='sm' c='dimmed'>
                Amounts are recorded as final totals in Indian rupees.
              </Text>
            </div>

            {categories.length === 0 && !categoriesQuery.isLoading && (
              <Alert color='blue' icon={<IconAlertCircle size={18} />}>
                Create the first expense category before saving an expense.
              </Alert>
            )}

            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              <TextInput
                type='date'
                label='Expense date'
                required
                value={expenseDate}
                onChange={(event) => setExpenseDate(event.currentTarget.value)}
              />
              <NumberInput
                label='Amount'
                required
                prefix='₹ '
                min={0.01}
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                thousandSeparator=','
                value={amount}
                onChange={setAmount}
              />
              <Select
                label='Category'
                placeholder={categories.length ? 'Select category' : 'Create a category first'}
                required
                searchable
                data={categories.map((category) => ({
                  value: category.id,
                  label: category.name,
                }))}
                value={categoryId}
                onChange={setCategoryId}
                disabled={categoriesQuery.isLoading || categories.length === 0}
                nothingFoundMessage='No category found'
              />
              <TextInput
                label='Paid to'
                placeholder='Vendor or payee'
                required
                maxLength={255}
                value={payee}
                onChange={(event) => setPayee(event.currentTarget.value)}
              />
            </SimpleGrid>

            <Group align='end'>
              <Textarea
                label='Notes'
                placeholder='Optional details'
                autosize
                minRows={2}
                maxRows={4}
                maxLength={2_000}
                value={notes}
                onChange={(event) => setNotes(event.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                type='button'
                variant='light'
                leftSection={<IconPlus size={17} />}
                onClick={() => setShowCategoryForm((visible) => !visible)}
              >
                Add category
              </Button>
            </Group>

            {showCategoryForm && (
              <Paper withBorder radius='md' p='md' bg='var(--mantine-color-gray-0)'>
                <Group align='end'>
                  <TextInput
                    label='New category'
                    placeholder='For example, Office supplies'
                    required
                    maxLength={100}
                    value={categoryName}
                    onChange={(event) => setCategoryName(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void submitCategory();
                      }
                    }}
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <Button type='button' loading={savingCategory} onClick={() => submitCategory()}>
                    Save category
                  </Button>
                  <Button
                    type='button'
                    variant='default'
                    onClick={() => {
                      setCategoryName('');
                      setShowCategoryForm(false);
                    }}
                  >
                    Cancel
                  </Button>
                </Group>
              </Paper>
            )}

            <div>
              <Button
                type='submit'
                loading={savingExpense}
                disabled={categoriesQuery.isLoading || categories.length === 0}
              >
                Save expense
              </Button>
            </div>
          </Stack>
        </form>
      </Paper>

      {expensesQuery.isError && (
        <Alert color='red' icon={<IconAlertCircle size={18} />}>
          Expenses could not be loaded.
        </Alert>
      )}

      <Paper withBorder radius='lg' className='data-card'>
        <Table.ScrollContainer minWidth={850}>
          <Table verticalSpacing='md' horizontalSpacing='lg' highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Paid to</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Notes</Table.Th>
                <Table.Th>Entered by</Table.Th>
                <Table.Th ta='right'>Amount</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {expensesQuery.data?.map((expense) => (
                <Table.Tr key={expense.id}>
                  <Table.Td>
                    <Text size='sm'>{dayjs(expense.expenseDate).format('DD MMM YYYY')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={600}>{expense.payee}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant='light' color='indigo'>
                      {expense.category.name}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm' c={expense.notes ? undefined : 'dimmed'} lineClamp={2}>
                      {expense.notes ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm' c='dimmed'>
                      {expense.createdBy.email}
                    </Text>
                  </Table.Td>
                  <Table.Td ta='right'>
                    <Text fw={700}>{inrFormatter.format(expense.amountPaise / 100)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!expensesQuery.isLoading && expensesQuery.data?.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c='dimmed' ta='center' py='xl'>
                      No expenses recorded yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
